import { track } from "@vercel/analytics";

/**
 * Conversion events for the public marketing funnel.
 *
 * The vocabulary is closed on purpose. The only question these events answer is
 * comparative — which surfaces push people into signup, and which ones they read
 * and leave — and that reading breaks the moment two call sites spell the same
 * intent differently. Vercel attaches the page URL to every event, so an id only
 * has to identify the button within its own page, not repeat where it lives.
 *
 * Marketing surfaces only. Anything a signed-in member does inside /app is
 * already recorded server-side in `usage_events` (see authorize_score_view.sql),
 * which is authoritative in a way a client-side beacon can never be.
 */
export type CtaId =
  /** Hero, stage 1 — the first thing a visitor can click. */
  | "hero-primary"
  /** Hero, final stage — clicked only after scrolling the whole hero. */
  | "hero-score"
  /** Under the product carousel — the highest-intent spot on the landing page. */
  | "carousel"
  /** The landing page's closing signup section. */
  | "landing-close"
  /** Landing, the "see the product" pointer into /preview. */
  | "landing-preview"
  /** Preview page, the closing signup CTA. */
  | "preview-cta"
  /** Pricing, Free tier card. */
  | "pricing-free"
  /** Pricing, closing CTA below the tiers. */
  | "pricing-close";

/**
 * Record a marketing CTA click. Safe to call anywhere: outside a Vercel
 * deployment (local dev, self-host) `track` is a no-op rather than a throw, so
 * call sites never need to guard it.
 */
export function trackCta(id: CtaId): void {
  track("cta_click", { cta: id });
}
